import {v4 as uuidv4} from "uuid";

import {
    RgsLimitTypeEnum,
    VerticalEnum,
} from "@atg-responsible-gambling-shared/limits-types";
import {fetchAuthorized} from "@atg-shared/auth";
import {PAYMENT_URL_NEW, RGS_SERVICE_URL} from "@atg-shared/service-url";

import type {
    CasinoTimeLimitsPayload,
    CasinoTimeLimitsResponse,
    DepositLimitsPayload,
    GetDepositLimitsResponse,
    NetLossLimitsResponse,
    LoginTimeLimitsPayload,
    LoginTimeLimitsResponse,
    NetLossLimitResponse,
    NetLossLimitsPayload,
    PutDepositLimitsResponse,
    RgsLimits,
    SimplifiedRgsLimits,
} from "@atg-responsible-gambling-shared/limits-types";

export type Limits = {
    day?: number;
    week?: number;
    month?: number;
};

export type RgsLimitsContext = {
    limitType?: RgsLimitTypeEnum;
    vertical?: VerticalEnum;
    set?: boolean;
};

const formatRgsPeriodic = (limits: Limits) =>
    Object.keys(limits).reduce(
        (a, k) => ({
            ...a,
            [k]: {limit: limits[k as keyof Limits]},
        }),
        {},
    );

const formatRgsLimits = (limits: SimplifiedRgsLimits) => {
    const formatRgsObj: {
        [key: string]: {week: {limit: number}};
    } = {};
    Object.entries(limits).map(([key, value]) => {
        formatRgsObj[key] = {week: {limit: value}};
        return null;
    });
    return formatRgsObj;
};

const DEPOSIT_LIMITS_URL = `${PAYMENT_URL_NEW}/deposit/limits`;
export const getDepositLimits = () =>
    fetchAuthorized<GetDepositLimitsResponse>(DEPOSIT_LIMITS_URL);

export const setDepositLimits = ({
    dailyLimit,
    weeklyLimit,
    monthlyLimit,
}: DepositLimitsPayload) =>
    fetchAuthorized<PutDepositLimitsResponse>(DEPOSIT_LIMITS_URL, {
        method: "PUT",
        body: JSON.stringify({
            businessOperationId: uuidv4(),
            depositLimits: {
                ...(dailyLimit !== undefined && {
                    dailyLimit: {limit: dailyLimit},
                }),
                ...(weeklyLimit !== undefined && {
                    weeklyLimit: {limit: weeklyLimit},
                }),
                ...(monthlyLimit !== undefined && {
                    monthlyLimit: {limit: monthlyLimit},
                }),
            },
            requestId: uuidv4(),
        }),
    });

export const getLoginTimeLimits = () =>
    fetchAuthorized<LoginTimeLimitsResponse>(`${RGS_SERVICE_URL}/logintimelimit`);

export const setLoginTimeLimits = ({
    dailyLimit,
    weeklyLimit,
    monthlyLimit,
}: LoginTimeLimitsPayload) =>
    fetchAuthorized<LoginTimeLimitsResponse>(`${RGS_SERVICE_URL}/logintimelimit`, {
        method: "PUT",
        body: JSON.stringify({
            [RgsLimitTypeEnum.LOGIN]: {
                day: {limit: dailyLimit},
                week: {limit: weeklyLimit},
                month: {limit: monthlyLimit},
            },
        }),
    });

export const getCasinoTimeLimits = () =>
    fetchAuthorized<CasinoTimeLimitsResponse>(`${RGS_SERVICE_URL}/playtimelimit/CASINO`);

export const setCasinoTimeLimits = (newLimits: CasinoTimeLimitsPayload) =>
    fetchAuthorized<CasinoTimeLimitsResponse>(`${RGS_SERVICE_URL}/playtimelimit`, {
        method: "PUT",
        body: JSON.stringify({
            [RgsLimitTypeEnum.TIME]: {
                CASINO: {day: {limit: newLimits.CASINO}},
            },
        }),
    });

export const getNetLossLimits = () =>
    fetchAuthorized<NetLossLimitsResponse>(`${RGS_SERVICE_URL}/netlosslimit`);

const getNetLossLimit = (vertical?: VerticalEnum) =>
    fetchAuthorized<NetLossLimitResponse>(
        `${RGS_SERVICE_URL}/netlosslimit${vertical ? `/${vertical}` : ""}`,
        {method: "GET"},
    );

export const setNetLossLimits = ({
    CASINO,
    HORSE_BETTING,
    SPORTSBOOK,
}: NetLossLimitsPayload) =>
    fetchAuthorized<NetLossLimitsResponse>(`${RGS_SERVICE_URL}/netlosslimit`, {
        method: "PUT",
        body: JSON.stringify({
            netLossLimits: {
                CASINO: CASINO ? {week: {limit: CASINO}} : undefined,
                HORSE_BETTING: HORSE_BETTING ? {week: {limit: HORSE_BETTING}} : undefined,
                SPORTSBOOK: SPORTSBOOK ? {week: {limit: SPORTSBOOK}} : undefined,
            },
        }),
    });

export const deleteNetLossLimit = (vertical: VerticalEnum) =>
    fetchAuthorized<NetLossLimitsResponse>(
        `${RGS_SERVICE_URL}/netlosslimit/${vertical}`,
        {
            method: "DELETE",
        },
    );

const getPlayTimeLimit = (vertical?: VerticalEnum) =>
    fetchAuthorized<RgsLimits>(
        `${RGS_SERVICE_URL}/playtimelimit${vertical ? `/${vertical}` : ""}`,
        {method: "GET"},
    );

const getVerticalRgsLimits = (vertical: VerticalEnum = VerticalEnum.CASINO) =>
    fetchAuthorized<RgsLimits>(`${RGS_SERVICE_URL}/${vertical}`, {
        method: "GET",
    });

const setNetLossLimit = (
    vertical?: VerticalEnum,
    loss: Limits | Partial<SimplifiedRgsLimits> = {},
) => {
    if (Object.keys(loss).includes("week")) {
        return fetchAuthorized<RgsLimits>(`${RGS_SERVICE_URL}/netlosslimit`, {
            method: "PUT",
            body: JSON.stringify({
                [RgsLimitTypeEnum.LOSS]: {
                    [`${vertical}`]: formatRgsPeriodic(loss as Limits),
                },
            }),
        });
    }

    return fetchAuthorized<RgsLimits>(`${RGS_SERVICE_URL}/netlosslimit`, {
        method: "PUT",
        body: JSON.stringify({
            [RgsLimitTypeEnum.LOSS]: formatRgsLimits(loss as SimplifiedRgsLimits),
        }),
    });
};

const setPlayTimeLimit = (vertical: VerticalEnum, time: Limits) =>
    fetchAuthorized<RgsLimits>(`${RGS_SERVICE_URL}/playtimelimit`, {
        method: "PUT",
        body: JSON.stringify({
            [RgsLimitTypeEnum.TIME]: {[`${vertical}`]: formatRgsPeriodic(time)},
        }),
    });

const setVerticalRgsLimits = (
    vertical: VerticalEnum = VerticalEnum.CASINO,
    loss: Limits,
    time: Limits,
) =>
    fetchAuthorized<RgsLimits>(`${RGS_SERVICE_URL}/${vertical}`, {
        method: "PUT",
        body: JSON.stringify({
            [RgsLimitTypeEnum.LOSS]: {[`${vertical}`]: formatRgsPeriodic(loss)},
            [RgsLimitTypeEnum.TIME]: {[`${vertical}`]: formatRgsPeriodic(time)},
        }),
    });

export const fetchRgsLimits = (
    context: Partial<RgsLimitsContext>,
    limits?: {loss?: Limits | SimplifiedRgsLimits; time?: Limits},
) => {
    switch (context.limitType) {
        case RgsLimitTypeEnum.LOSS:
            return limits?.loss
                ? setNetLossLimit(context.vertical, limits.loss)
                : getNetLossLimit(context.vertical);
        case RgsLimitTypeEnum.TIME:
            return context.vertical && limits?.time
                ? setPlayTimeLimit(context.vertical, limits.time)
                : getPlayTimeLimit(context.vertical);
        default:
            return limits?.loss && limits.time
                ? setVerticalRgsLimits(
                      context.vertical,
                      limits.loss as Limits,
                      limits.time,
                  )
                : getVerticalRgsLimits(context.vertical);
    }
};
